import React from 'react';
import { getPlayerFullName, getPlayerNameColor } from '../../utils/playerUtils';
import { getInitials } from '../../utils/nameUtils';
import { getGenderAvatarBg } from '../../utils/genderColors';
import StatusBadge from '../common/StatusBadge';

export function pegBoardPickerFromQueue(pegBoardQueue, playersById) {
  const id = pegBoardQueue?.waitlistIds?.[0];
  if (!id) return null;
  return playersById?.[id] || null;
}

/**
 * Informational strip on an empty Peg Board court. Does not occupy a player slot.
 */
const PegBoardPickerHint = ({ player, compact = false }) => {
  if (!player) return null;
  const displayName = getPlayerFullName(player);
  return (
    <div
      className={`flex min-w-0 items-center gap-1.5 rounded-lg border border-primary/20 bg-primary/5 ${
        compact ? 'px-2 py-1' : 'px-2.5 py-1.5'
      }`}
      title={`${displayName} is first in the queue and picks the next game`}
    >
      <span
        className={`w-4 shrink-0 text-right font-semibold tabular-nums text-text-muted ${
          compact ? 'text-[10px]' : 'text-xs'
        }`}
      >
        1
      </span>
      <span
        className={`flex shrink-0 items-center justify-center rounded-full font-semibold text-text-inverse ${
          compact ? 'h-6 w-6 text-[10px]' : 'h-7 w-7 text-xs'
        } ${getGenderAvatarBg(player.gender)}`}
        aria-hidden
      >
        {getInitials(displayName)}
      </span>
      <p className="min-w-0 flex-1 truncate">
        <span className="sr-only">Up to pick: </span>
        <span className={`font-semibold ${compact ? 'text-xs' : 'text-sm'} ${getPlayerNameColor(player.gender)}`}>
          {displayName}
        </span>
      </p>
      <StatusBadge variant="primary" size="small" className="!px-1.5 !py-0.5 shrink-0 text-[10px] uppercase tracking-wide">
        To pick
      </StatusBadge>
    </div>
  );
};

/** Empty Peg Board court copy. Pick game is passed as `action` when this slot is the pick target. */
export const PegBoardWaitingForGame = ({ compact = false, action = null }) => (
  <div
    className="flex w-full flex-1 flex-col items-center justify-center gap-3 text-center"
    role="status"
  >
    {action || (
      <p className={`font-medium text-text-muted ${compact ? 'text-sm' : 'text-sm lg:text-base'}`}>
        Waiting for game
      </p>
    )}
  </div>
);

/** Shown on later empty courts so the picker only appears on the slot a pick will fill. */
export const PegBoardFillFirstHint = ({ targetLabel, compact = false }) => {
  if (!targetLabel) return null;
  return (
    <div
      className={`flex min-w-0 items-center rounded-lg border border-black/10 bg-black/[0.03] dark:border-white/10 dark:bg-white/[0.04] ${
        compact ? 'px-2 py-1' : 'px-2.5 py-1.5'
      }`}
      role="status"
    >
      <p className={`min-w-0 font-medium text-text-secondary ${compact ? 'text-xs' : 'text-sm'}`}>
        Next pick is on {targetLabel}
      </p>
    </div>
  );
};

/** Banner on the court the viewer should pick — replaces the To pick name row. */
export const PegBoardYourTurnHint = ({ compact = false }) => (
  <div
    className={`flex min-w-0 items-center justify-between gap-1.5 rounded-lg border border-primary/25 bg-primary/5 ${
      compact ? 'px-2 py-1' : 'px-2.5 py-1.5'
    }`}
    role="status"
  >
    <p className={`min-w-0 font-semibold text-primary ${compact ? 'text-xs' : 'text-sm'}`}>
      Pick the next game
    </p>
    <StatusBadge variant="primary" size="small" className="!px-1.5 !py-0.5 shrink-0 text-[10px] uppercase tracking-wide">
      Your turn
    </StatusBadge>
  </div>
);

export default PegBoardPickerHint;
